import { HelpCircle } from "lucide-react";

export type FloatingHelpButtonProps = {
	/** Whether the help drawer is currently open. The FAB hides while open. */
	open: boolean;
	/** Called when the user clicks the FAB to request the drawer be opened. */
	onOpen: () => void;
};

/**
 * Global Help affordance — a circular FAB pinned to the bottom-right of the
 * viewport. Replaces the per-sidebar Help button (Phase 7 follow-up after
 * shipping `aed941b..df7717b`): the sidebar trigger sat in the SYSTEM section
 * above Settings, but real-world use surfaced two issues —
 *   1. Discoverability was poor when the sidebar was collapsed.
 *   2. The trigger was tied to one navigation surface; the FAB floats over
 *      the entire authenticated app instead.
 *
 * As of the non-modal-drawer conversion, drawer state lives in the parent
 * (`routes/_app.tsx`) so the layout can apply a `pr-[480px]` right-padding
 * on `<main>` while the drawer is open. This component is purely the trigger
 * — it does NOT own state. It hides itself when `open` is true so it does
 * not sit visually underneath the right-anchored 480px panel.
 *
 * Color rationale: `#4f46e5` is the documented brand-primary indigo (see
 * `apps/web/src/styles.css` `--color-brand-primary` and
 * `.claude/rules/frontend.md` brand palette callout). No new raw hex.
 */
export function FloatingHelpButton({ open, onOpen }: FloatingHelpButtonProps) {
	if (open) return null;
	return (
		<button
			type="button"
			onClick={onOpen}
			aria-label="Open help"
			className="fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-[#4f46e5] text-white shadow-lg transition-colors duration-150 hover:bg-[#4338ca] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f46e5] focus-visible:ring-offset-2"
		>
			<HelpCircle className="h-6 w-6" strokeWidth={1.75} aria-hidden="true" />
		</button>
	);
}
